'use client';

import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { useNetworkActivity } from '@/hooks/useNetworkActivity';

interface NetworkWaveformProps {
  width?: number;
  height?: number;
  color?: string;
  singleDirection?: boolean;
}

export const NetworkWaveform: FC<NetworkWaveformProps> = ({
  width = 200,
  height = 16,
  color = '#fff',
  singleDirection = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { history } = useNetworkActivity();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.clearRect(0, 0, width, height);

    const barCount = Math.floor(width / 2); // 1px bar + 1px gap
    const displayHistory = history.slice(-barCount);

    const taperBars = 10;

    const startX = (barCount - displayHistory.length) * 2;

    displayHistory.forEach((value, i) => {
      const totalBars = displayHistory.length;
      let taperFactor = 1;

      if (i < taperBars) {
        taperFactor = i / taperBars;
      } else if (i >= totalBars - taperBars) {
        taperFactor = (totalBars - 1 - i) / taperBars;
      }

      // Variable opacity: taller bars more opaque (range: 30% - 100%)
      const opacity = 0.3 + value * 0.7;
      ctx.fillStyle = color;
      ctx.globalAlpha = opacity * taperFactor;

      const x = startX + i * 2;

      if (singleDirection) {
        const barHeight = value * (height - 2) * taperFactor;
        ctx.fillRect(x, height - barHeight, 1, barHeight);
      } else {
        const centerY = height / 2;
        const barHeight = value * (height / 2 - 1) * taperFactor;
        ctx.fillRect(x, centerY - barHeight, 1, barHeight);
        ctx.fillRect(x, centerY, 1, barHeight);
      }
    });

    ctx.globalAlpha = 1;
  }, [history, width, height, color, singleDirection]);

  return <canvas ref={canvasRef} style={{ width, height, display: 'block' }} />;
};
